import type { AudioManager } from '../../audio/AudioManager';
import type { PointerEvent2, TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import {
  drawSilhouette,
  type SilhouetteId,
  worldHandAnchor,
} from '../../render/SilhouetteRenderer';
import { drawBlade } from '../../render/WeaponRenderer';
import type {
  AttackKind,
  AttackPattern,
  Enemy,
  Hero,
  Vec2,
  Weapon,
} from '../../types';
import type { Phase } from '../Phase';
import { isDefenseCorrect, resolveDefense } from '../systems/Combat';
import { drawCombatBackground } from '../../render/CombatBackground';

const ICON_TAP_RADIUS = 42;
const HEAVY_HOLD_FRACTION = 0.7; // must hold for 70% of window

type Stage = 'windup' | 'window' | 'resolve' | 'done';

export interface DefendOutcome {
  pattern: AttackPattern;
  correct: boolean;
  hpLoss: number;
  durabilityLoss: number;
  fatigueGain: number;
}

export class DefendSubphase implements Phase {
  private hero: Hero;
  private enemy: Enemy;
  private weapon: Weapon;
  private audio: AudioManager;
  private pattern: AttackPattern;
  private heroSilhouette: SilhouetteId;
  private enemySilhouette: SilhouetteId;

  private stage: Stage = 'windup';
  private stageT = 0; // seconds in current stage
  private stageDur = 0;

  private done = false;
  private unbindDown: (() => void) | null = null;
  private unbindUp: (() => void) | null = null;

  // Icon position cached at WINDUP entry so it's stable for the whole round.
  private iconPos: Vec2 = { x: 0, y: 0 };
  // Response tracking (during the WINDOW stage).
  private tappedOnIconAt: number | null = null; // ms since window opened
  private holdActive = false;
  private holdReleasedAt: number | null = null; // ms since window opened
  private tappedOffIcon = false;

  private outcome: DefendOutcome | null = null;
  private resolveT = 0;
  /** Visual: enemy attack swing progress 0..1 during window/resolve. */
  private swingT = 0;
  private shakeAmount = 0;

  constructor(
    hero: Hero,
    enemy: Enemy,
    weapon: Weapon,
    pattern: AttackPattern,
    heroSilhouette: SilhouetteId,
    enemySilhouette: SilhouetteId,
    input: TouchInput,
    audio: AudioManager,
  ) {
    this.hero = hero;
    this.enemy = enemy;
    this.weapon = weapon;
    this.audio = audio;
    this.pattern = pattern;
    this.heroSilhouette = heroSilhouette;
    this.enemySilhouette = enemySilhouette;
    this.iconPos = this.computeIconPos();
    this.enterStage('windup');

    this.unbindDown = input.onPointerDown((e) => this.handleDown(e));
    this.unbindUp = input.onPointerUp(() => this.handleUp());
  }

  private computeIconPos(): Vec2 {
    // Position above the enemy's head, with small per-attack jitter to keep
    // the player's eyes on the enemy zone instead of memorising a fixed spot.
    const baseX = LOGICAL_WIDTH * 0.72;
    const baseY = 200;
    return {
      x: baseX + (Math.random() - 0.5) * 36,
      y: baseY + (Math.random() - 0.5) * 24,
    };
  }

  private enterStage(s: Stage): void {
    this.stage = s;
    this.stageT = 0;
    switch (s) {
      case 'windup':
        this.stageDur = this.pattern.windupMs / 1000;
        break;
      case 'window':
        this.stageDur = this.pattern.windowMs / 1000;
        this.tappedOnIconAt = null;
        this.holdActive = false;
        this.holdReleasedAt = null;
        this.tappedOffIcon = false;
        break;
      case 'resolve':
        this.stageDur = 0.55;
        break;
      case 'done':
        this.stageDur = 0;
        break;
    }
  }

  private handleDown(e: PointerEvent2): void {
    if (this.stage !== 'window') return;
    const dx = e.pos.x - this.iconPos.x;
    const dy = e.pos.y - this.iconPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist <= ICON_TAP_RADIUS) {
      this.tappedOnIconAt = this.stageT * 1000;
      this.holdActive = true;
    } else {
      this.tappedOffIcon = true;
    }
  }

  private handleUp(): void {
    if (this.stage !== 'window') return;
    if (this.holdActive) {
      this.holdReleasedAt = this.stageT * 1000;
      this.holdActive = false;
    }
  }

  update(dt: number): void {
    if (this.done) return;
    this.stageT += dt;

    if (this.stage === 'window') {
      // The visual swing animation runs through window + resolve.
      this.swingT = Math.min(1, this.stageT / this.stageDur);
    } else if (this.stage === 'resolve') {
      this.swingT = Math.min(1, 1 + this.resolveT * 0.5);
      this.resolveT += dt;
      this.shakeAmount = Math.max(0, this.shakeAmount - dt * 24);
    }

    if (this.stageT >= this.stageDur) {
      switch (this.stage) {
        case 'windup':
          this.enterStage('window');
          break;
        case 'window':
          this.evaluate();
          this.enterStage('resolve');
          break;
        case 'resolve':
          this.done = true;
          this.enterStage('done');
          break;
        case 'done':
          break;
      }
    }
  }

  private evaluate(): void {
    let response: 'tap' | 'hold' | 'none' = 'none';
    if (this.tappedOnIconAt !== null) {
      const heldUntil = this.holdReleasedAt ?? this.pattern.windowMs;
      const holdDur = heldUntil - this.tappedOnIconAt;
      const required = this.pattern.windowMs * HEAVY_HOLD_FRACTION;
      response = holdDur >= required ? 'hold' : 'tap';
    } else if (this.tappedOffIcon && this.pattern.kind === 'feint') {
      // An off-icon swing during a feint is still a false swing (durability cost).
      response = 'tap';
    }
    const correct = isDefenseCorrect(this.pattern, response);
    const { hpLoss, durabilityLoss, fatigueGain } = resolveDefense(this.pattern, correct);
    this.outcome = { pattern: this.pattern, correct, hpLoss, durabilityLoss, fatigueGain };
    if (correct) {
      if (this.pattern.kind === 'heavy') this.audio.play('strike_perfect');
      else if (this.pattern.kind === 'quick') this.audio.play('strike_good');
      else this.audio.play('bellows_tap'); // soft sound for "wisely held"
      this.shakeAmount = 3;
    } else {
      this.audio.play('strike_miss');
      this.shakeAmount = 9;
    }
  }

  isComplete(): boolean {
    return this.done;
  }

  getOutcome(): DefendOutcome | null {
    return this.outcome;
  }

  dispose(): void {
    this.unbindDown?.();
    this.unbindUp?.();
    this.unbindDown = null;
    this.unbindUp = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const shakeX = (Math.random() - 0.5) * this.shakeAmount;
    const shakeY = (Math.random() - 0.5) * this.shakeAmount;
    ctx.save();
    ctx.translate(shakeX, shakeY);

    drawCombatBackground(ctx, this.enemy.backgroundId);
    this.drawCharacters(ctx);
    this.drawTelegraph(ctx);
    this.drawOutcomeFlash(ctx);
    this.drawHud(ctx);

    ctx.restore();
  }

  private drawCharacters(ctx: CanvasRenderingContext2D): void {
    const heroX = LOGICAL_WIDTH * 0.28;
    const enemyX = LOGICAL_WIDTH * 0.72;
    const feetY = 470;
    // Hero idle sway.
    const heroSway = this.stageT * 1.2 + 0;
    // Enemy attack pose: lean forward during window/resolve.
    const enemySway = this.stageT * 1.6 + 1.3;
    const enemyRecoil = this.stage === 'window' ? this.swingT * 10 : this.stage === 'resolve' ? 14 : 0;
    drawSilhouette(ctx, this.heroSilhouette, {
      cx: heroX,
      feetY,
      swayPhase: heroSway,
      flip: false,
      recoil: this.outcome && !this.outcome.correct ? -6 : 0,
      tilt: this.outcome && !this.outcome.correct ? -0.04 : 0,
    });
    // Draw the weapon in the hero's hand.
    const anchor = worldHandAnchor(this.heroSilhouette, heroX, feetY, false);
    drawBlade(ctx, this.weapon.shape, this.weapon.heat, {
      cx: anchor.x + 50,
      cy: anchor.y - 8,
      length: 92,
      rotation: anchor.rotation - 0.6,
      damage: this.weapon.damage,
    });
    drawSilhouette(ctx, this.enemySilhouette, {
      cx: enemyX,
      feetY,
      swayPhase: enemySway,
      flip: true,
      recoil: -enemyRecoil,
      tilt: this.stage === 'resolve' && this.outcome?.correct ? 0.05 : 0,
    });
  }

  private drawTelegraph(ctx: CanvasRenderingContext2D): void {
    if (this.stage === 'windup' || this.stage === 'window') {
      drawAttackIcon(ctx, this.pattern.kind, this.iconPos.x, this.iconPos.y, {
        windupT: this.stage === 'windup' ? this.stageT / this.stageDur : 1,
        windowT: this.stage === 'window' ? this.stageT / this.stageDur : 0,
        radius: ICON_TAP_RADIUS,
      });
    }
  }

  private drawOutcomeFlash(ctx: CanvasRenderingContext2D): void {
    if (this.stage !== 'resolve' || !this.outcome) return;
    const t = this.resolveT;
    const alpha = Math.max(0, 0.45 - t * 1.2);
    ctx.fillStyle = this.outcome.correct
      ? `rgba(180, 240, 200, ${alpha})`
      : `rgba(220, 80, 60, ${alpha})`;
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
    // Verdict text.
    ctx.fillStyle = this.outcome.correct ? '#a8e0a8' : '#ff8060';
    ctx.font = 'bold 24px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const label = verdictLabel(this.pattern.kind, this.outcome.correct);
    ctx.fillText(label, LOGICAL_WIDTH / 2, 130);
  }

  private drawHud(ctx: CanvasRenderingContext2D): void {
    // Hero HP bar (top-left).
    drawBar(ctx, 14, 14, 140, 12, this.hero.hp / this.hero.maxHp, '#7fffaa', `${this.hero.name}`);
    // Enemy fatigue bar (top-right).
    drawBar(
      ctx,
      LOGICAL_WIDTH - 14 - 140,
      14,
      140,
      12,
      this.enemy.fatigue / this.enemy.fatigueThreshold,
      '#ffd060',
      this.enemy.displayName,
      true,
    );
    // Weapon durability pip below hero.
    const dPct = Math.max(0, this.weapon.durability) / 60;
    drawBar(ctx, 14, 32, 100, 6, dPct, '#c9b896', 'BLADE');
  }
}

function drawBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  pct: number,
  color: string,
  label: string,
  rightAlign = false,
): void {
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = color;
  ctx.fillRect(x + 1, y + 1, Math.max(0, (w - 2) * pct), h - 2);
  ctx.strokeStyle = '#3a3530';
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = '#c9b896';
  ctx.font = '10px system-ui, sans-serif';
  ctx.textAlign = rightAlign ? 'right' : 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(label, rightAlign ? x + w : x, y + h + 2);
}

function verdictLabel(kind: AttackKind, correct: boolean): string {
  if (correct) {
    return kind === 'feint' ? 'NOT FOOLED' : kind === 'heavy' ? 'BLOCKED' : 'PARRIED';
  }
  return kind === 'feint' ? 'FALSE SWING!' : 'HIT!';
}

interface IconDrawOpts {
  windupT: number; // 0..1 during windup
  windowT: number; // 0..1 during action window
  radius: number;
}

export function drawAttackIcon(
  ctx: CanvasRenderingContext2D,
  kind: AttackKind,
  x: number,
  y: number,
  opts: IconDrawOpts,
): void {
  const armed = opts.windowT > 0;
  // Scale grows during windup (telegraph), then pulses during window.
  const baseScale = 0.4 + opts.windupT * 0.6;
  const pulse = armed ? 1 + 0.15 * Math.sin(opts.windowT * Math.PI * 8) : 1;
  const scale = baseScale * pulse;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  if (kind === 'quick') {
    ctx.strokeStyle = armed ? '#ffd84a' : '#ffb84a';
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    // Curved arrow.
    ctx.beginPath();
    ctx.arc(0, 0, 22, -0.4 * Math.PI, 0.5 * Math.PI);
    ctx.stroke();
    // Arrowhead.
    ctx.beginPath();
    ctx.moveTo(0, 22);
    ctx.lineTo(-7, 16);
    ctx.lineTo(7, 16);
    ctx.closePath();
    ctx.fillStyle = ctx.strokeStyle;
    ctx.fill();
  } else if (kind === 'heavy') {
    ctx.strokeStyle = armed ? '#ff8050' : '#c46040';
    ctx.lineWidth = 7;
    ctx.lineCap = 'round';
    // Big double-arrow downward.
    ctx.beginPath();
    ctx.moveTo(-14, -22);
    ctx.lineTo(0, 6);
    ctx.lineTo(14, -22);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-14, 0);
    ctx.lineTo(0, 28);
    ctx.lineTo(14, 0);
    ctx.stroke();
    // Tap hint ring.
    if (armed) {
      ctx.strokeStyle = 'rgba(255,128,80,0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, 30, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else {
    // Feint: flickering yellow question-glyph.
    const flicker = 0.4 + 0.6 * (Math.sin(opts.windowT * Math.PI * 22) * 0.5 + 0.5);
    ctx.fillStyle = `rgba(255, 230, 90, ${flicker})`;
    ctx.strokeStyle = `rgba(255, 230, 90, ${flicker})`;
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    // Question hook
    ctx.beginPath();
    ctx.arc(0, -8, 12, Math.PI, 0);
    ctx.lineTo(12, 4);
    ctx.lineTo(0, 4);
    ctx.stroke();
    // Dot
    ctx.beginPath();
    ctx.arc(0, 18, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();

  // Faint tap-target ring during the window so the player has a clear hit area.
  if (armed && kind !== 'feint') {
    ctx.strokeStyle = `rgba(255, 220, 120, ${0.25 + 0.25 * Math.sin(opts.windowT * Math.PI * 6)})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y, opts.radius, 0, Math.PI * 2);
    ctx.stroke();
  }
}
